import { useNavigate } from 'react-router-dom';
import { useState, useEffect, useCallback, useMemo } from 'react';

import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Table from '@mui/material/Table';
import Button from '@mui/material/Button';
import TableBody from '@mui/material/TableBody';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import TableContainer from '@mui/material/TableContainer';
import TablePagination from '@mui/material/TablePagination';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';

import { useRouter } from 'src/routes/hooks';
import { useBoolean } from 'src/hooks/use-boolean';
import { useTable } from 'src/hooks/use-table';

import { getAllBranchesWithPagination, deleteBranch, checkBranchDeletable, type BranchDto } from 'src/api/branch_api_data';

import { Iconify } from 'src/components/iconify';
import { Scrollbar } from 'src/components/scrollbar';

import { TableNoData } from '../table-no-data';
import { BranchTableRow } from '../branch-table-row';
import { TableEmptyRows } from '../table-empty-rows';
import { BranchTableHead } from '../branch-table-head';
import { BranchTableToolbar } from '../branch-table-toolbar';
import BranchExportModal from '../components/BranchExportModal';
import { emptyRows, applyFilter, getComparator } from '../utils';

const TABLE_HEAD = [
  { id: 'name', label: 'Şube Adı' },
  { id: 'companyName', label: 'Şirket Adı' },
  { id: 'address', label: 'Adres' },
  { id: 'phone', label: 'Telefon' },
  { id: 'responsibleUsernames', label: 'Sorumlular' },
  { id: 'branchOpeningTime', label: 'Açılış' },
  { id: 'branchClosingTime', label: 'Kapanış' },
  { id: 'dailyEmployeeCount', label: 'Çalışan Sayısı' },
  { id: 'status', label: 'Durum' },
  { id: '', label: '' },
];

// Storage keys for pagination persistence
const STORAGE_KEY_PAGE_SIZE = 'qr-admin-table-state:branch:pageSize';
const STORAGE_KEY_PAGE = 'qr-admin-table-state:branch:page';

// Helper functions for localStorage persistence
const getStoredPageSize = (): number => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY_PAGE_SIZE);
    return stored ? parseInt(stored, 10) : 5;
  } catch {
    return 5;
  }
};

const getStoredPage = (): number => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY_PAGE);
    return stored ? parseInt(stored, 10) : 0;
  } catch {
    return 0;
  }
};

// ----------------------------------------------------------------------

export function BranchView() {
  const table = useTable();
  const router = useRouter();
  const confirm = useBoolean();
  const exportModal = useBoolean();
  const navigate = useNavigate();

  const [branches, setBranches] = useState<BranchDto[]>([]);
  const [filterName, setFilterName] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [branchToDelete, setBranchToDelete] = useState<BranchDto | null>(null);
  
  // Pagination state with localStorage persistence
  const [currentPage, setCurrentPage] = useState(getStoredPage);
  const [pageSize, setPageSize] = useState(getStoredPageSize);
  
  // Sorting state
  const [order, setOrder] = useState<'asc' | 'desc'>('asc');
  const [orderBy, setOrderBy] = useState('name');
  
  // Company filter state
  const [filterCompany, setFilterCompany] = useState<string>('');
  
  // Get unique company names for filter dropdown
  const companyOptions = useMemo(() => {
    const companies = new Set<string>();
    branches.forEach(branch => {
      if (branch.companyName) {
        companies.add(branch.companyName);
      }
    });
    return Array.from(companies).sort();
  }, [branches]);

  const fetchBranches = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Tüm şubeleri tek seferde çek (server-sorted) — arama/filtre/sayfalama client-side.
      // Şube sayısı küçük olduğundan büyük size ile tek sayfada hepsini alıyoruz.
      const data = await getAllBranchesWithPagination(0, 1000, orderBy, order);

      setBranches(data.content || []);
    } catch (err: any) {
      console.error('Error fetching branches:', err);
      setError(err.message || 'Şubeler yüklenirken bir hata oluştu.');
      setBranches([]);
    } finally {
      setLoading(false);
    }
  }, [orderBy, order]);

  useEffect(() => {
    fetchBranches();
  }, [fetchBranches]);

  const handleDeleteRow = useCallback(
    (id: string) => {
      const branchToDeleteInfo = branches.find((branch) => branch.id === id);
      if (branchToDeleteInfo) {
        setBranchToDelete(branchToDeleteInfo);
        confirm.onTrue();
      }
    },
    [confirm, branches]
  );

  const handleConfirmDelete = useCallback(async () => {
    if (branchToDelete) {
      try {
        const deletabilityCheck = await checkBranchDeletable(branchToDelete.id);
        if (!deletabilityCheck.canDelete) {
          alert(deletabilityCheck.message);
          confirm.onFalse();
          return;
        }

        await deleteBranch(branchToDelete.id);
        await fetchBranches();
        confirm.onFalse();
        setBranchToDelete(null);
      } catch (err: any) {
        console.error('Failed to delete branch:', err);
        setError(err.message || 'Şube silinirken bir hata oluştu.');
        confirm.onFalse();
      }
    }
  }, [branchToDelete, fetchBranches, confirm]);

  // Frontend filtreleme
  const filteredBranches = useMemo(() => {
    let filtered = branches;
    
    // Company filter
    if (filterCompany) {
      filtered = filtered.filter(branch => 
        branch.companyName === filterCompany
      );
    }
    
    // Name/search filter
    if (filterName.trim()) {
      filtered = filtered.filter((branch) => 
        branch.name.toLowerCase().includes(filterName.toLowerCase()) ||
        branch.companyName?.toLowerCase().includes(filterName.toLowerCase()) ||
        branch.address?.toLowerCase().includes(filterName.toLowerCase()) ||
        branch.phone?.toLowerCase().includes(filterName.toLowerCase()) ||
        branch.responsibleUsernames?.some(username => 
          username.toLowerCase().includes(filterName.toLowerCase())
        )
      );
    }
    
    return filtered;
  }, [branches, filterName, filterCompany]);

  // Client-side sayfalama: filtrelenmiş tüm set üzerinden geçerli sayfayı dilimle
  const paginatedBranches = useMemo(
    () => filteredBranches.slice(currentPage * pageSize, currentPage * pageSize + pageSize),
    [filteredBranches, currentPage, pageSize]
  );

  const notFound = !filteredBranches.length && !loading;

  const handleEditRow = useCallback(
    (id: string) => {
      router.push(`/dashboard/branches/${id}/edit`);
    },
    [router]
  );

  const handleFilterByName = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setCurrentPage(0); // 0-based indexing
      setFilterName(event.target.value);
    },
    []
  );

  const handleClearSearch = useCallback(() => {
    setFilterName('');
    setFilterCompany('');
    setCurrentPage(0);
    localStorage.setItem(STORAGE_KEY_PAGE, '0');
  }, []);

  const handleChangePage = useCallback((event: unknown, newPage: number) => {
    setCurrentPage(newPage);
    localStorage.setItem(STORAGE_KEY_PAGE, newPage.toString());
  }, []);

  const handleChangeRowsPerPage = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const newPageSize = parseInt(event.target.value, 10);
    setPageSize(newPageSize);
    setCurrentPage(0);
    localStorage.setItem(STORAGE_KEY_PAGE_SIZE, newPageSize.toString());
    localStorage.setItem(STORAGE_KEY_PAGE, '0');
  }, []);

  const handleFilterByCompany = useCallback((company: string) => {
    setFilterCompany(company);
    setCurrentPage(0);
    localStorage.setItem(STORAGE_KEY_PAGE, '0');
  }, []);

  const handleSort = useCallback((id: string) => {
    const isAsc = orderBy === id && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(id);
  }, [orderBy, order]);

  const handleGotoCreatePage = () => {
    navigate('/dashboard/branches/create');
  };

  return (
    <>
      <Container maxWidth={false}>
        <Box display="flex" alignItems="center" mb={5}>
          <Typography variant="h4" flexGrow={1}>
            Şubeler
          </Typography>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              variant="outlined"
              color="primary"
              startIcon={<Iconify icon="eva:download-outline" />}
              onClick={exportModal.onTrue}
            >
              Dışa Aktar
            </Button>
            <Button
              variant="contained"
              color="inherit"
              startIcon={<Iconify icon="mingcute:add-line" />}
              onClick={handleGotoCreatePage}
            >
              Yeni Şube
            </Button>
          </Box>
        </Box>

        {error && (
          <Box sx={{ mb: 3, p: 2, bgcolor: 'error.light', color: 'error.contrastText', borderRadius: 1 }}>
            {error}
          </Box>
        )}

        <Card>
          <BranchTableToolbar
            filterName={filterName}
            onFilterName={handleFilterByName}
            onClearSearch={handleClearSearch}
            numSelected={table.selected.length}
            onExport={exportModal.onTrue}
            filterCompany={filterCompany}
            onFilterCompany={handleFilterByCompany}
            companyOptions={companyOptions}
          />

          <Scrollbar>
            {loading ? (
              <Box sx={{ p: 3, textAlign: 'center' }}>
                <Typography>Yükleniyor...</Typography>
              </Box>
            ) : (
              <TableContainer sx={{ overflow: 'unset' }}>
                <Table sx={{ minWidth: 800 }}>
                  <BranchTableHead
                    order={order}
                    orderBy={orderBy}
                    rowCount={paginatedBranches?.length || 0}
                    numSelected={table.selected.length}
                    onSort={handleSort}
                    onSelectAllRows={(checked) =>
                      table.onSelectAllRows(
                        checked,
                        (paginatedBranches || []).map((branch) => branch.id)
                      )
                    }
                    headLabel={TABLE_HEAD}
                  />
                  <TableBody>
                    {paginatedBranches.map((row) => (
                      <BranchTableRow
                        key={row.id}
                        row={row}
                        selected={table.selected.includes(row.id)}
                        onSelectRow={() => table.onSelectRow(row.id)}
                        onDeleteRow={() => handleDeleteRow(row.id)}
                        onEditRow={() => handleEditRow(row.id)}
                      />
                    ))}

                    {notFound && <TableNoData searchQuery={filterName} />}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Scrollbar>

          <TablePagination
            component="div"
            page={currentPage}
            count={filteredBranches.length}
            rowsPerPage={pageSize}
            onPageChange={handleChangePage}
            rowsPerPageOptions={[5, 10, 25]}
            onRowsPerPageChange={handleChangeRowsPerPage}
            labelRowsPerPage="Sayfa başına satır:"
            labelDisplayedRows={({ from, to, count }) =>
              `${from}-${to} / ${count !== -1 ? count : `${to}'den fazla`}`
            }
          />
        </Card>
      </Container>

      <Dialog
        open={confirm.value}
        onClose={confirm.onFalse}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
      >
        <DialogTitle id="delete-dialog-title">Şubeyi Sil</DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-dialog-description">
            &quot;{branchToDelete?.name}&quot; şubesini silmek istediğinizden emin misiniz? Bu işlem
            geri alınamaz.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={confirm.onFalse} color="inherit">
            İptal
          </Button>
          <Button onClick={handleConfirmDelete} color="error" variant="contained">
            Sil
          </Button>
        </DialogActions>
      </Dialog>

      <BranchExportModal
        open={exportModal.value}
        onClose={exportModal.onFalse}
        currentFilters={{
          searchTerm: filterName
        }}
        availableBranches={branches}
      />
    </>
  );
}